// @flow

import * as React from 'react';
import { QueryWithPartialRefetchUntilReactApollo220Release as Query } from '@ta/social.feed';
import { connect } from 'react-redux';
import Logging from '@ta/common.logging';
import { SuggestedFolloweesLoadingSkeleton } from '@ta/social.skeletons';
import { RecommendedFolloweesImpressionIdContext } from '@ta/tracking.interactions';
import { featureIsEnabled } from '@ta/common.features';
import type {
  SuggestedFollowee,
  SuggestedFolloweesTypeInput,
  SuggestedFolloweesPlacementInput,
} from '@ta/social.member-types';

import RecommendedFolloweesQuery from './RecommendedFollowees.gql';

type ExternalProps = {|
  /** children is a function that accepts a suggestedFollowees render props */
  children: (SuggestedFollowee[]) => React.Node,
  /** A callback executed in the event of an error */
  onError?: (error: any) => void,
  /** number of followees to fetch */
  limit?: number,
  /** suggested followee types to fetch */
  types?: SuggestedFolloweesTypeInput[],
  /** the placement on the site where the suggested followees will appear */
  placement?: SuggestedFolloweesPlacementInput,
  /** location id to scope relevant suggestions */
  locationId?: number,
  /** display loading skeleton while fetching data, defaults to true */
  useLoadingSkeleton?: boolean,
|};

type Props = {|
  ...ExternalProps,
  /** locale to filter relevant suggestions */
  locale: string,
|};

/**
 * Provides a list of suggested followees.
 *
 * ------ IMPORTANT -----------
 * This query SHOULD NOT depend on the logged in user id, since we need to ensure the data is relatively static.
 * Otherwise, logging in will force a requery and may not match the original data when not logged in
 * See TRVX-9508 for original ticket
 * */
const RecommendedFolloweeDataProvider = (props: Props) => (
  <Query
    errorPolicy="all"
    query={RecommendedFolloweesQuery}
    variables={{
      locationId: props.locationId,
      limit: props.limit,
      locale: props.locale,
      types: props.types,
      placement: props.placement,
    }}
    onError={props.onError}
  >
    {({ data, loading, error }) => {
      const insertFollowee = (followees, followeeToInsert, limit) => {
        /** do not insert a (sponsored) profile if already following */
        const isFollowing =
          followeeToInsert &&
          followeeToInsert.userProfile &&
          followeeToInsert.userProfile.isFollowing;

        if (!followees || !followeeToInsert || isFollowing) {
          return followees;
        }
        const filtered = followees.filter(
          followee =>
            followee != null &&
            followee.userProfile &&
            followeeToInsert.userProfile &&
            followee.userProfile.userId !== followeeToInsert.userProfile.userId,
        );
        filtered.unshift(followeeToInsert);
        while (filtered.length > limit) {
          filtered.pop();
        }
        return filtered;
      };
      if (error) {
        Logging.error(error);
      }
      if (data && data.recommendedFollowees && data.recommendedFollowees.suggestedFollowees) {
        let filteredFollowees = data.recommendedFollowees.suggestedFollowees.filter(
          candidate => candidate != null && candidate.userProfile != null,
        );

        // ADS-5434: Viking/Austria Home Hero Sponsorship
        if (props.locationId === 1 && featureIsEnabled('adsol_home_hero_test')) {
          let sponsorProfile;
          // Insert campaign feature check here and insert hard coded sponsor profile if exists
          if (featureIsEnabled('adsol_home_hero_viking_austria') && data.sponsoredProfileViking) {
            sponsorProfile = data.sponsoredProfileViking;
          }
          if (sponsorProfile) {
            filteredFollowees = insertFollowee(
              filteredFollowees,
              { userProfile: sponsorProfile },
              props.limit,
            );
          }
        }

        return (
          <RecommendedFolloweesImpressionIdContext.Provider
            value={{ recommendedFolloweesImpressionId: data.recommendedFollowees.impressionId }}
          >
            {props.children(filteredFollowees)}
          </RecommendedFolloweesImpressionIdContext.Provider>
        );
      }
      if (loading) {
        return props.useLoadingSkeleton ? <SuggestedFolloweesLoadingSkeleton /> : null;
      }
      return props.children([]);
    }}
  </Query>
);

RecommendedFolloweeDataProvider.defaultProps = {
  types: ['FEATURED', 'FOLLOWERS', 'FRIENDS_OF_FRIENDS', 'GEO'],
  placement: 'ONBOARDING',
  onError: undefined,
  limit: 3,
  useLoadingSkeleton: true,
};

export default connect<Props, ExternalProps, _, _, _, _>((state, ownProps: ExternalProps) => ({
  locationId: ownProps.locationId || state.page.geoId || null,
  locale: state.i18n.locale,
}))(RecommendedFolloweeDataProvider);
